import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import classNames from 'classnames';
import { Link as GLink } from 'gatsby';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import React from 'react';

const useStyles = makeStyles()((theme) => ({
  image: { position: 'inherit !important' as any },
  root: {
    display: 'block',
    boxShadow: theme.shadows[8],
    border: `2px solid ${theme.palette.primary.dark}`,
    borderRadius: theme.shape.borderRadius,
    willChange: 'transform',
    transition: theme.transitions.create(
      ['border', 'box-shadow', 'transform'],
      {
        duration: theme.transitions.duration.shorter,
      },
    ),
    transform: 'scale(1)',
    backgroundColor: theme.palette.background.default,
    overflow: 'hidden',
    '&:hover': {
      transform: 'scale(0.99)',
      boxShadow: theme.shadows[16],
      border: `2px solid ${theme.palette.primary.main}`,
    },
  },
  content: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    textAlign: 'center',
  },
  contentWithGradient: {
    background:
      'radial-gradient(rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 60%, rgba(0,0,0,0) 100%)',
  },
  text: { color: '#ddd' },
  title: {
    textTransform: 'uppercase',
    letterSpacing: 2,
    color:
      theme.palette.primary.main === '#00cccc'
        ? theme.palette.common.white
        : theme.palette.primary.light,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    padding: theme.spacing(2),
    margin: 'auto',
  },
  titleWithGradient: {
    //  background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 35%, rgba(0,0,0,0.4) 65%, rgba(0,0,0,0) 100%)',
  },
  secondary: {
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: `calc(${theme.typography.body1.lineHeight} + ${theme.spacing(
      2,
    )}px)`,
  },
  corner: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
  },
  topLeft: {
    paddingRight: theme.spacing(1.5),
    borderRadius: `${theme.shape.borderRadius}px 0 ${theme.spacing(2)} 0`,
    borderRight: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  topRight: {
    paddingLeft: theme.spacing(1.5),
    borderRadius: `0 ${theme.shape.borderRadius}px 0 ${theme.spacing(2)}`,
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  bottomLeft: {
    paddingRight: theme.spacing(1.5),
    borderRadius: `0 ${theme.spacing(2)} 0 ${theme.shape.borderRadius}px`,
    borderRight: `1px solid ${theme.palette.divider}`,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  bottomRight: {
    paddingLeft: theme.spacing(1.5),
    borderRadius: `${theme.spacing(2)} 0 ${theme.shape.borderRadius}px 0`,
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  placeholder: {
    width: '100%',
    paddingTop: '56.25%',
  },
}));

interface ImageCardProps {
  className?: string;
  image?: IGatsbyImageData;
  title: string;
  link: string;
  topLeft?: React.ReactNode;
  topRight?: React.ReactNode;
  bottomLeft?: React.ReactNode;
  bottomRight?: React.ReactNode;
}

function ImageCard({
  className,
  link,
  image,
  title,
  topLeft,
  topRight,
  bottomLeft,
  bottomRight,
}: ImageCardProps) {
  const { classes } = useStyles();
  return (
    <GLink to={link} className={classNames(classes.root, className)}>
      {(image && (
        <GatsbyImage
          image={image}
          alt={`Image Card of ${title}`}
          className={classes.image}
        />
      )) || <div className={classes.placeholder} />}

      <div
        className={classNames(classes.content, {
          [classes.contentWithGradient]: !!image,
        })}
      >
        {(topLeft || topRight) && (
          <div className={classes.secondary}>
            {topLeft && (
              <Typography
                variant="body2"
                component="span"
                className={classNames(
                  classes.corner,
                  classes.topLeft,
                  classes.text,
                )}
                noWrap
              >
                {topLeft}
              </Typography>
            )}

            {topRight && (
              <Typography
                variant="body2"
                component="span"
                className={classNames(
                  classes.corner,
                  classes.topRight,
                  classes.text,
                )}
                noWrap
              >
                {topRight}
              </Typography>
            )}
          </div>
        )}

        <Typography
          variant="h5"
          className={classNames(classes.title, {
            [classes.titleWithGradient]: !!image,
          })}
        >
          {title}
        </Typography>

        {(bottomLeft || bottomRight) && (
          <div className={classes.secondary}>
            {bottomLeft && (
              <Typography
                variant="body2"
                component="span"
                className={classNames(
                  classes.corner,
                  classes.bottomLeft,
                  classes.text,
                )}
                noWrap
              >
                {bottomLeft}
              </Typography>
            )}

            {bottomRight && (
              <Typography
                variant="body2"
                component="span"
                className={classNames(
                  classes.corner,
                  classes.bottomRight,
                  classes.text,
                )}
                noWrap
              >
                {bottomRight}
              </Typography>
            )}
          </div>
        )}
      </div>
    </GLink>
  );
}

export default ImageCard;
